import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Upload, X, CheckCircle, AlertCircle, Image, FileText, Loader2, RefreshCw, Eye } from 'lucide-react';
import api, { getAssetUrl } from '@/utils/api';

const FileUploader = ({
  label,
  accept = "image/*",
  uploadEndpoint = "/upload/logo",
  currentFile = null,
  onUploadSuccess,
  onRemove,
  maxSizeMB = 5,
  helpText = "",
  showPreview = true,
  previewType = "image", // "image" or "document"
  squareCrop = false, // when true, show crop/pad selector for logo uploads
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [cropMode, setCropMode] = useState('crop'); // 'crop' or 'pad' (for square logo)
  const [pendingFile, setPendingFile] = useState(null);
  const inputRef = useRef(null);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return false;
    }

    // Check file type
    const acceptedTypes = accept.split(',').map(t => t.trim());
    const isAccepted = acceptedTypes.some(type => {
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type === '.pdf') return file.type === 'application/pdf';
      return file.type === type;
    });

    if (!isAccepted) {
      setError('Invalid file type');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    
    if (!validateFile(file)) {
      return;
    }

    // Show local preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }

    if (squareCrop) {
      // Hold the file - user will pick crop mode and click upload
      setPendingFile(file);
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file, retry = 0, mode = null) => {
    setUploading(true);
    setUploadProgress(0);
    setRetryCount(retry);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (squareCrop) {
        formData.append('crop_mode', mode || cropMode);
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await api.post(uploadEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 60 second timeout
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.data.path) {
        onUploadSuccess?.(response.data.path);
        if (squareCrop) {
          toast.success(`Logo uploaded as 1:1 square (${response.data.crop_mode || mode || cropMode})`);
        } else {
          toast.success('File uploaded successfully');
        }
        setError(null);
        setPendingFile(null);
        setPreview(response.data.path);
      } else {
        throw new Error('No path returned');
      }
    } catch (err) {
      console.error('Upload error:', err);
      
      // Retry logic - up to 3 times
      if (retry < 2) {
        toast.info(`Upload failed, retrying... (${retry + 2}/3)`);
        setTimeout(() => uploadFile(file, retry + 1, mode), 1000);
        return;
      }
      
      const errorMessage = err.response?.data?.detail || err.message || 'Upload failed';
      setError(errorMessage);
      toast.error(errorMessage);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = (mode) => {
    if (!pendingFile) return;
    setCropMode(mode);
    uploadFile(pendingFile, 0, mode);
  };

  const handleRetry = () => {
    if (inputRef.current?.files?.[0]) {
      uploadFile(inputRef.current.files[0]);
    } else {
      inputRef.current?.click();
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
    onRemove?.();
  };

  const displayUrl = preview || (currentFile ? getImageUrl(currentFile) : null);
  const isDocument = previewType === 'document' || currentFile?.toLowerCase()?.endsWith('.pdf');

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Square Crop Selector — shown after file pick when squareCrop=true */}
      {squareCrop && pendingFile && preview && !uploading && (
        <Card data-testid="logo-crop-selector" className="p-4 border-teal-200 bg-teal-50/40">
          <p className="text-sm font-medium text-slate-800 mb-3">Choose how to make the logo 1:1 square:</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setCropMode('crop')}
              data-testid="crop-mode-crop"
              className={`p-2 rounded-lg border-2 transition ${cropMode === 'crop' ? 'border-teal-500 bg-white' : 'border-slate-200 bg-white/60 hover:border-slate-300'}`}
            >
              <div className="aspect-square w-full mb-2 rounded overflow-hidden border bg-slate-100">
                <img src={preview} alt="Center crop preview" className="w-full h-full object-cover" />
              </div>
              <p className="text-xs font-medium text-slate-700">Center crop</p>
              <p className="text-[10px] text-slate-500">Crops edges to a square. Looks tight.</p>
            </button>
            <button
              type="button"
              onClick={() => setCropMode('pad')}
              data-testid="crop-mode-pad"
              className={`p-2 rounded-lg border-2 transition ${cropMode === 'pad' ? 'border-teal-500 bg-white' : 'border-slate-200 bg-white/60 hover:border-slate-300'}`}
            >
              <div className="aspect-square w-full mb-2 rounded overflow-hidden border bg-white flex items-center justify-center">
                <img src={preview} alt="Pad preview" className="max-w-full max-h-full object-contain" />
              </div>
              <p className="text-xs font-medium text-slate-700">Pad with white</p>
              <p className="text-[10px] text-slate-500">Preserves whole logo with white background.</p>
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              type="button"
              size="sm"
              onClick={() => handleConfirmUpload(cropMode)}
              data-testid="logo-confirm-upload"
              className="bg-teal-600 hover:bg-teal-700 flex-1"
            >
              Upload as 1:1 ({cropMode === 'crop' ? 'Center Crop' : 'Padded'})
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setPendingFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ''; }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Current File or Upload Area */}
      {displayUrl && !uploading && !pendingFile ? (
        <Card className="p-4 border-slate-200">
          <div className="flex items-start gap-4">
            {/* Preview */}
            {showPreview && (
              <div className="flex-shrink-0">
                {isDocument ? (
                  <div className="w-20 h-20 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
                    <FileText className="w-10 h-10 text-red-500" />
                  </div>
                ) : (
                  <img
                    src={displayUrl}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f1f5f9" width="80" height="80"/%3E%3Ctext x="40" y="45" text-anchor="middle" fill="%2394a3b8" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                )}
              </div>
            )}
            
            {/* Info & Actions */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">File uploaded</span>
              </div>
              <p className="text-xs text-slate-500 truncate max-w-[200px]">
                {currentFile?.split('/').pop() || 'Uploaded file'}
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Replace
                </Button>
                {currentFile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(getImageUrl(currentFile), '_blank')}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-3 h-3 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${uploading ? 'border-teal-300 bg-teal-50' : error ? 'border-red-300 bg-red-50' : 'border-slate-300 hover:border-teal-400 hover:bg-teal-50'}
          `}
        >
          {uploading ? (
            <div className="space-y-3">
              <Loader2 className="w-10 h-10 mx-auto text-teal-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-slate-700">Uploading...</p>
                {retryCount > 0 && (
                  <p className="text-xs text-amber-600">Retry {retryCount + 1}/3</p>
                )}
              </div>
              <Progress value={uploadProgress} className="h-2 max-w-[200px] mx-auto" />
            </div>
          ) : error ? (
            <div className="space-y-3">
              <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
              <div>
                <p className="text-sm font-medium text-red-700">{error}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                  className="mt-2"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3">
                {previewType === 'document' ? (
                  <FileText className="w-6 h-6 text-slate-400" />
                ) : (
                  <Image className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <p className="text-sm font-medium text-slate-700">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {accept.includes('pdf') ? 'JPG, PNG or PDF' : 'JPG, PNG, GIF or WebP'}
                {` (max ${maxSizeMB}MB)`}
              </p>
            </>
          )}
        </div>
      )}
      
      {helpText && (
        <p className="text-xs text-slate-500">{helpText}</p>
      )}
    </div>
  );
};

export default FileUploader;
